import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface IGAutomation {
  _id: string;
  keyword: string;
  enabled: boolean;
  updatedAt: string;
  runCount?: number;
}

export function Instagram() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState<IGAutomation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  async function fetchAutomations() {
    try {
      const response = await fetch(`/api/automations/instagram`);
      if (response.ok) {
        const data = await response.json();
        setAutomations(data.automations || []);
      } else {
        console.error("Failed to fetch automations");
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchAutomations();
    setBreadcrumbs([
      { label: "Flows", path: "/automations" },
      { label: "Instagram", path: "/automations/instagram" },
    ]);

    // Cleanup: clear breadcrumbs when component unmounts
    return () => {
      clearBreadcrumbs();
    };
  }, []);

  async function handleCreate() {
    setIsCreating(true);
    try {
      const response = await fetch(`/api/automations/instagram`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        await fetchAutomations();
      } else {
        console.error("Failed to create automation");
      }
    } catch (error) {
      console.error("Failed to create automation:", error);
    } finally {
      setIsCreating(false);
    }
  }

  function formatDate(dateString?: string) {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return "-";
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Instagram Flows</h1>
        <Button onClick={handleCreate} disabled={isCreating}>
          {isCreating ? "Creating..." : "Create"}
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Keyword</TableHead>
            <TableHead className="text-right">Runs</TableHead>
            <TableHead className="text-right">Updated At</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {automations && automations.length > 0 ? (
            automations.map((automation) => (
              <TableRow 
                key={automation._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/automations/instagram/${automation._id}/edit`)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        automation.enabled
                          ? "bg-green-500 hover:bg-green-600 text-white border-transparent"
                          : "bg-gray-500 hover:bg-gray-600 text-white border-transparent"
                      }
                    >
                      {automation.enabled ? "Live" : "Draft"}
                    </Badge>
                    <span>{automation.keyword || "-"}</span>
                  </div>
                </TableCell>
                <TableCell 
                  className="text-right"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/automations/instagram/${automation._id}/runs`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <span className="hover:underline text-blue-600 hover:text-blue-800">
                    {automation.runCount ?? 0}
                  </span>
                </TableCell>
                <TableCell className="text-right">{formatDate(automation.updatedAt)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="h-24 text-center">
                No flows found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

